/**
 * The command bar: ⌘K or Ctrl+K anywhere, or the search box in the top bar.
 *
 * Built on Mantine's Spotlight for the shortcut, the modal, the focus trap
 * and giving focus back when it closes. The search box, the keyboard and the
 * highlight are this file's own, because the bar does more than pick from a
 * list: it reads a filter language (search/query.ts), autocompletes it
 * (search/suggest.ts) and previews the highlighted result.
 *
 * What it offers:
 *
 *   - pages, things to start, Settings tabs and appearance, from commands.ts,
 *     which takes the pages from navFor() so permissions hold;
 *   - the small lists the app already caches (segments, campaigns,
 *     destinations, buyers, suppliers, files, email templates), filtered
 *     here in the browser. Only lists whose page is on this person's rail are
 *     fetched or shown;
 *   - people, first, from `GET /api/v1/people?q=` with free text after a
 *     pause: one row per person, for anyone who may see people;
 *   - records, from `POST /api/v1/leads/search` with whatever was typed:
 *     free text after two characters and a pause, filters at once when one
 *     is finished. The server scopes that search to the person asking, as it
 *     does the list page;
 *   - for the highlighted result, a preview and a few quick actions, each
 *     offered only to someone allowed to use it.
 *
 * Everything is read only while the bar is open: the modal mounts nothing
 * while it is closed.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Group, Kbd, Loader, Text } from '@mantine/core';
import { useDebouncedValue, useMediaQuery, useOs } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Spotlight, spotlight } from '@mantine/spotlight';
import {
  IconCheck, IconClock, IconDeviceFloppy, IconHistory, IconSearch, IconSparkles, IconTable, IconUserCircle,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  segmentFilters, useCampaigns, useCommandLeadSearch, useDestinations, useEmailTemplates, useEntities,
  useCampaignCustomKeys, useFilterTabs, useInterpretSearch, useLeadCustomKeys, useLeadFacets, useLeadsByIds,
  useSearchInterpretStatus, useSegmentPeople, useSheets,
  type DashboardStats, type LeadFilters, type SegmentCounts,
} from '../api/queries';
import { sharedSegmentRows, useSharedSegments } from './sharedSegmentCommands';
import { useCompanies, useDeals } from '../api/crm';
import { useTasks } from '../api/activities';
import { usePeople, type PersonSummary } from '../api/people';
import { dueText } from '../pages/crm/Timeline';
import { useNavUser } from './navUser';
import { useAppearance } from '../appearance';
import { LeadDetailModal } from '../pages/LeadDetailModal';
import { TOKENS, formatNumber } from '../theme';
import { SpeedChip } from '../ui/SpeedChip';
import { navLabel, useVocab, type Vocab } from '../vocab';
import {
  canSeeList, groupOf, iconFor, LIST_PAGE, matches, routeFor, seesPeople, staticCommands,
  type Command, type CommandMeta, type CommandUser, type GroupKey, type ListKey,
} from './commands';
import { pushRecent, readRecent, type Recent } from './commandRecent';
import { ChipRow, chipErrorText, type RowItem } from './search/chips';
import { orErrorText } from './search/chipErrors';
import type { LeadsHandoff } from './search/handoff';
import { HelpPanel } from './search/HelpPanel';
import { LIST_HEIGHT, PreviewPane, type LeadRow, type QuickAction } from './search/Preview';
import {
  chipFor, commitTerms, isEmptyBody, joinTerms, londonToday, mergeNames, orProblem, parseParts, parseQuery,
  splitInterpreted, tidyTerms, toQueryBody, tokenize, wantsInterpreting,
  type Chip, type Named, type QueryContext,
} from './search/query';
import { pushRecentQuery, readRecentQueries } from './search/recentQueries';
import { readRecentLeads } from './search/recentLeads';
import { CommandRowContent, LeadRowContent } from './search/rows';
import {
  chosenCampaigns, suggest, type CampaignKeys, type Suggestion, type SuggestProperty,
} from './search/suggest';
import { useProperties } from '../api/properties';
import { useScoringSettings } from '../api/scoring';
import { useScoringInUse } from '../pages/scoring/scoringShared';

/** Opens the bar from anywhere, such as the top bar's search box. */
export const openCommandBar = () => spotlight.open();

/** How long typing must pause before free text is searched. */
export const LEAD_SEARCH_DEBOUNCE = 250;
/** Most rows a cached list shows for one query; the rest are a page away. */
const PER_LIST = 5;
/** People offered for some free text: one row each. */
const PEOPLE_HITS = 5;
/** Wide enough for the results and the preview side by side. */
const WIDE = '(min-width: 64em)';

export function CommandBar() {
  // The same user as the rail, so a page missing there is missing here.
  const user = useNavUser();
  const [leadId, setLeadId] = useState<string | null>(null);
  const wide = useMediaQuery(WIDE) ?? false;

  return (
    <>
      <Spotlight.Root
        // ⌘K should work from inside a field too: it types nothing, so it
        // cannot get in the way of what someone is writing.
        tagsToIgnore={[]}
        maxHeight={640}
        size={wide ? 980 : 640}
        styles={{
          content: { background: TOKENS.surface3, boxShadow: 'var(--rs-shadow-pop)' },
          body: { background: TOKENS.surface3 },
          actionsList: { borderColor: TOKENS.border, maxHeight: LIST_HEIGHT },
          footer: { borderColor: TOKENS.border },
        }}
      >
        <Bar user={user} wide={wide} onLead={setLeadId} />
      </Spotlight.Root>
      {/* The same dialog the list page opens, so a record found here reads
          and behaves exactly as it does there. */}
      <LeadDetailModal id={leadId} onClose={() => setLeadId(null)} />
    </>
  );
}

function groupLabel(v: Vocab, key: GroupKey): string {
  switch (key) {
    case 'recent': return 'Recent';
    case 'pages': return 'Pages';
    case 'actions': return 'Actions';
    case 'settings': return 'Settings';
    case 'appearance': return 'Appearance';
    case 'segments': return navLabel(v, 'segments');
    case 'people': return navLabel(v, 'people');
    case 'companies': return navLabel(v, 'companies');
    case 'deals': return navLabel(v, 'deals');
    case 'tasks': return navLabel(v, 'tasks');
    case 'campaigns': return navLabel(v, 'campaigns');
    case 'destinations': return navLabel(v, 'destinations');
    case 'buyers': return navLabel(v, 'buyers');
    case 'suppliers': return navLabel(v, 'suppliers');
    case 'imports': return navLabel(v, 'imports');
    case 'templates': return 'Email templates';
    case 'leads': return navLabel(v, 'leads');
  }
}

type Lists = {
  lists: Partial<Record<ListKey, Command[]>>;
  loaded: Set<ListKey>;
  /** The names a typed filter is matched against; null where this person may not see the list. */
  named: { campaigns: Named[] | null; suppliers: Named[] | null; buyers: Named[] | null };
};

/** Each cached list, as commands. Fetched only for someone whose rail has its page. */
function useListCommands(user: CommandUser, v: Vocab): Lists {
  const qc = useQueryClient();
  const show = (k: ListKey) => canSeeList(user, k);
  const segments = useFilterTabs(show('segments')).data;
  const sharedSegments = useSharedSegments(show('segments')).data;
  const campaigns = useCampaigns(show('campaigns')).data;
  const destinations = useDestinations(show('destinations')).data;
  const buyers = useEntities('buyers', show('buyers')).data;
  const suppliers = useEntities('suppliers', show('suppliers')).data;
  const sheets = useSheets(show('imports')).data;
  const templates = useEmailTemplates(show('templates')).data;
  const companies = useCompanies({ page_size: 500 }, show('companies')).data;
  const deals = useDeals({ page_size: 200 }, show('deals')).data;
  // My open tasks, soonest due first: what someone reaching for a task by
  // name most likely means.
  const tasks = useTasks({ owner: 'me', due: 'all', page_size: 200 }, show('tasks')).data;

  // Figures only when another screen has already fetched them: the bar never
  // runs the dashboard's or the segment counts' queries itself.
  const today = new Map(
    (qc.getQueryData<DashboardStats>(['dashboard', 'today'])?.top_campaigns ?? []).map((c) => [c.campaign_id, c.leads]),
  );
  // The leads page counts people too for someone who sees them, under its own key.
  const segmentKey = segments?.tabs.map((t) => ({ id: t.id, filter_state: segmentFilters(t) }));
  const segmentCounts = segmentKey
    ? qc.getQueryData<SegmentCounts>(['segment-counts', segmentKey])
      ?? qc.getQueryData<SegmentCounts>(['segment-counts', segmentKey, 'people'])
    : undefined;

  const lists: Partial<Record<ListKey, Command[]>> = {};
  const loaded = new Set<ListKey>();
  const put = (
    k: ListKey,
    rows: { id: string; label: string; description?: string; meta?: CommandMeta }[] | undefined,
    prefix: string,
  ) => {
    // A cached copy from elsewhere in the app is still only shown to someone allowed the list.
    if (!rows || !show(k)) return;
    loaded.add(k);
    lists[k] = rows.map((r) => {
      const id = `${prefix}:${r.id}`;
      return {
        id,
        group: k,
        label: r.label,
        description: r.description,
        icon: iconFor(LIST_PAGE[k]),
        run: { kind: 'go', to: routeFor(id) ?? LIST_PAGE[k] },
        meta: r.meta,
      };
    });
  };
  put('segments', segments && [...segments.tabs.map((t) => ({
    id: String(t.id), label: t.name,
    meta: { kind: 'segment', id: t.id, count: segmentCounts?.totals.get(t.id) ?? null } as CommandMeta,
  })), ...sharedSegmentRows(sharedSegments?.segments)], 'segment');
  put('companies', companies?.companies.map((c) => ({
    id: c.id, label: c.name, description: c.domain || v.One('company'),
  })), 'company');
  put('deals', deals?.deals.map((d) => ({
    id: d.id, label: d.name,
    description: [d.company_name, d.stage_name].filter(Boolean).join(' · ') || v.One('deal'),
  })), 'deal');
  put('tasks', tasks?.tasks.map((t) => ({
    id: t.id, label: t.subject,
    description: [dueText(t.due_at), t.links[0]?.label].filter(Boolean).join(' · '),
  })), 'task');
  put('campaigns', campaigns?.campaigns.map((c) => ({
    id: c.id, label: c.name,
    meta: {
      kind: 'campaign', id: c.id, active: c.active, isReference: c.is_reference_list,
      groups: c.group_count, destinations: c.destination_count, today: today.get(c.id) ?? null,
    },
  })), 'campaign');
  put('destinations', destinations?.destinations.map((d) => ({ id: d.id, label: d.name })), 'destination');
  put('buyers', buyers?.entities.map((e) => ({
    id: e.id, label: e.name, description: v.One('buyer'),
    meta: { kind: 'entity', entity: 'buyer', id: e.id, active: e.active },
  })), 'buyer');
  put('suppliers', suppliers?.entities.map((e) => ({
    id: e.id, label: e.name, description: v.One('supplier'),
    meta: { kind: 'entity', entity: 'supplier', id: e.id, active: e.active },
  })), 'supplier');
  put('imports', sheets?.sheets.map((s) => ({ id: s.id, label: s.file_name, description: s.description ?? undefined })), 'sheet');
  put('templates', templates?.templates.map((t) => ({
    id: t.id, label: t.name, description: t.subject || undefined,
    meta: { kind: 'template', subject: t.subject, updatedAt: t.updated_at },
  })), 'template');

  const named = {
    campaigns: show('campaigns') && campaigns ? campaigns.campaigns.map((c) => ({ id: c.id, name: c.name })) : null,
    suppliers: show('suppliers') && suppliers ? suppliers.entities.map((e) => ({ id: e.id, name: e.name })) : null,
    buyers: show('buyers') && buyers ? buyers.entities.map((e) => ({ id: e.id, name: e.name })) : null,
  };
  return { lists, loaded, named };
}

/** One selectable row. */
type Entry =
  | { type: 'suggestion'; s: Suggestion }
  | { type: 'command'; cmd: Command }
  | { type: 'lead'; lead: LeadRow }
  | { type: 'search'; action: 'open' | 'save' }
  | { type: 'recent-query'; query: string }
  | { type: 'recent-lead'; lead: LeadRow }
  | { type: 'person'; person: PersonSummary }
  | { type: 'interpret' };


type Section = { key: string; label: string; entries: Entry[]; extra?: ReactNode; empty?: string };

function Bar({ user, wide, onLead }: { user: CommandUser; wide: boolean; onLead: (id: string) => void }) {
  const v = useVocab();
  const navigate = useNavigate();
  const appearance = useAppearance();
  const os = useOs();
  const mod = os === 'macos' || os === 'ios' ? '⌘' : 'Ctrl';
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pendingCaret = useRef<number | null>(null);

  /** Finished filters, each a written-out term such as `status:sold`. */
  const [committed, setCommitted] = useState<string[]>([]);
  /** What is still in the box: free text and any filter being typed. */
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [sel, setSel] = useState(0);
  const [recent, setRecent] = useState<Recent[]>(() => readRecent());
  const [recentQueries, setRecentQueries] = useState<string[]>(() => readRecentQueries());
  /** Ids only; the records are fetched afresh, scoped, below. */
  const [recentLeadIds] = useState<string[]>(() => readRecentLeads());
  /** What plain English left as text, for a quiet note. */
  const [interpretNote, setInterpretNote] = useState<string | null>(null);
  const interpret = useInterpretSearch();

  const canLeads = canSeeList(user, 'leads');
  // People are never offered to a scoped login: no group, no repeats: key.
  const canPeople = seesPeople(user);
  const all = staticCommands(user, v);
  const { lists, loaded, named } = useListCommands(user, v);
  const today = useMemo(() => londonToday(), []);

  // Fetched only once a custom field or a source tag is being typed.
  const peek = suggest(text, caret, {
    campaigns: null, suppliers: null, buyers: null, sources: null, customKeys: null, today,
  }, v).wants;
  const customKeys = useLeadCustomKeys(canLeads && peek === 'custom_keys').data?.keys ?? null;
  // Lead properties: their keys first after `custom.`, with labels, and
  // their options, yes or no, or comparisons after the colon.
  const propertyDefs = useProperties('submission', { enabled: canLeads && peek === 'custom_keys' }).data?.properties;
  const properties: SuggestProperty[] | null = useMemo(
    () => propertyDefs?.map((d) => ({ key: d.key, label: d.label, type: d.type, options: d.options.map((o) => o.value) })) ?? null,
    [propertyDefs],
  );
  const sources = useLeadFacets(canLeads && peek === 'sources').data?.sources ?? null;

  // `owner:` is the CRM's, for someone who sees people: members by name.
  const ownership = canPeople;
  const members = useSegmentPeople(ownership).data?.people ?? null;
  const owners = members
    ? members.filter((p) => !['buyer', 'supplier', 'unapproved'].includes(p.role)).map((p) => ({ id: p.id, name: p.name }))
    : null;
  // `score:` and `band:`: only where the organisation scores, and never for
  // a scoped login (scores are internal).
  const scoring = useScoringInUse();
  const bandList = useScoringSettings(scoring).data?.bands;
  const bands = useMemo(() => bandList?.map((b) => ({ id: b.key, name: b.label })) ?? null, [bandList]);
  const ctx: QueryContext = { ...named, sources, today, people: canPeople, ownership, owners, scoring, bands };
  const committedChips: Chip[] = committed
    .map((term) => parseQuery(term, ctx).chips[0])
    .filter((c): c is Chip => c !== undefined);
  const parsed = parseQuery(text, ctx);
  // The finished terms and the box, as one query: with OR, order matters.
  const full = parseParts([...committed, text], ctx);
  // Still typing the last word: a half-written filter is not an error yet.
  const typing = text !== '' && !/\s$/.test(text);
  const pendingChips = parsed.chips.filter((c) => !(typing && c.end >= text.length));
  const chips = full.chips;
  const orError = orProblem(full);

  // The chip row: finished terms (removable), then what the box is read as.
  const rowItems: RowItem[] = [];
  committed.forEach((term, index) => {
    const t = tokenize(term)[0];
    if (t?.op) rowItems.push({ kind: 'op', op: t.op, pending: false });
    else {
      const chip = parseQuery(term, ctx).chips[0];
      if (chip) rowItems.push({ kind: 'chip', chip, pending: false, index });
    }
  });
  for (const t of mergeNames(tokenize(text), ctx)) {
    if (typing && t.end >= text.length) continue;
    if (t.op) rowItems.push({ kind: 'op', op: t.op, pending: true });
    else if (t.key) rowItems.push({ kind: 'chip', chip: chipFor(t, ctx), pending: true });
    // Free text is shown only as one side of an OR; otherwise the box says it.
    else if (full.hasOr && t.value.trim() && !/^-?custom\./i.test(t.raw)) {
      rowItems.push({ kind: 'text', text: t.value.trim(), pending: true });
    }
  }
  // With a campaign in the query, its own fields are offered first.
  const chosen = chosenCampaigns(chips);
  const scoped = useCampaignCustomKeys(chosen.map((c) => c.id), canLeads && peek === 'custom_keys').data;
  const campaignKeys: CampaignKeys | null = scoped
    ? {
      campaigns: chosen.filter((c) => scoped.campaign_ids.includes(c.id)),
      defined: scoped.defined,
      seen: scoped.keys,
    }
    : null;
  const body = toQueryBody(full);
  const hasFilters = chips.some((c) => c.filter && !c.error);
  const hasOr = full.hasOr;
  const savable = hasFilters && !hasOr && body.repeats_min == null;
  const help = text.trim() === '?' && committed.length === 0;
  const firstError = [...committedChips, ...pendingChips].find((c) => c.error)?.error ?? null;
  const errorText = orError ? orErrorText(orError) : firstError ? chipErrorText(firstError, v) : null;

  // Free text waits for a pause; a finished filter runs at once.
  const bodyKey = help || orError || isEmptyBody(body) ? null : JSON.stringify(body);
  const immediate = !bodyKey?.includes('"search"') && !typing;

  // Recently viewed records: ids from this browser, records fetched afresh.
  const idle = !help && committed.length === 0 && text.trim() === '';
  const recentLeads = useLeadsByIds(recentLeadIds, canLeads && idle).data?.leads ?? [];
  const recentLeadRows = recentLeadIds
    .map((id) => recentLeads.find((l) => l.id === id))
    .filter((l): l is LeadRow => l !== undefined);

  // Plain English: offered for three or more words that are no filter at all.
  const aiAvailable = useSearchInterpretStatus(canLeads).data?.available ?? false;
  const offerInterpret = canLeads && aiAvailable && committed.length === 0 && !help && wantsInterpreting(text, ctx);
  const [runKey, setRunKey] = useState<string | null>(null);
  useEffect(() => {
    if (bodyKey === null) {
      setRunKey(null);
      return undefined;
    }
    if (immediate) {
      setRunKey(bodyKey);
      return undefined;
    }
    const t = setTimeout(() => setRunKey(bodyKey), LEAD_SEARCH_DEBOUNCE);
    return () => clearTimeout(t);
  }, [bodyKey, immediate]);
  const runBody = useMemo(() => (runKey ? (JSON.parse(runKey) as LeadFilters) : null), [runKey]);
  const leadSearch = useCommandLeadSearch(runBody, canLeads);
  const searching = canLeads && bodyKey !== null;
  const fresh = runKey === bodyKey && !leadSearch.isPlaceholderData;
  const busy = searching && (!fresh || leadSearch.isFetching);
  const leadRows = searching && fresh ? (leadSearch.data?.leads ?? []) : [];

  const sugg = help ? null : suggest(text, caret, { ...ctx, customKeys, campaignKeys, properties }, v);

  // People matching free text, after the same pause as records. Only for
  // plain free text: a filter is a search of rows, not of people.
  const peopleText = !help && chips.length === 0 && !hasOr ? parsed.text.trim() : '';
  const [peopleQuery] = useDebouncedValue(peopleText, LEAD_SEARCH_DEBOUNCE);
  const peopleSearch = usePeople(
    { q: peopleQuery, page_size: PEOPLE_HITS },
    canPeople && peopleQuery.length >= 2 && peopleQuery === peopleText,
  );
  const personRows = canPeople && peopleQuery.length >= 2 && peopleQuery === peopleText && !peopleSearch.isPlaceholderData
    ? (peopleSearch.data?.people ?? [])
    : [];

  // ---- sections, in display order -------------------------------------
  const sections: Section[] = [];
  if (offerInterpret) {
    sections.push({ key: 'interpret', label: 'Plain English', entries: [{ type: 'interpret' }] });
  }
  for (const g of sugg?.groups ?? []) {
    sections.push({ key: `s-${g.key}`, label: g.label, entries: g.items.map((s) => ({ type: 'suggestion', s })) });
  }

  const byId = new Map<string, Command>();
  for (const c of [...all, ...Object.values(lists).flat()]) byId.set(c.id, c);
  const cmd = (c: Command): Entry => ({ type: 'command', cmd: c });
  const leadsSection = (): Section => ({
    key: 'leads',
    label: groupLabel(v, 'leads'),
    entries: leadRows.map((lead) => ({ type: 'lead', lead })),
    extra: busy
      ? <Loader size={12} aria-label="Searching" />
      : (
        <Group gap={8} wrap="nowrap">
          {leadSearch.data && (
            <Text size="xs" c="dimmed" data-testid="cmdk-count">{`${formatNumber(leadSearch.data.pagination.total)} found`}</Text>
          )}
          <SpeedChip stats={leadSearch.data?.query_stats} />
        </Group>
      ),
    empty: busy ? undefined : 'None found',
  });
  const searchActions = (): Section => ({
    key: 'this-search',
    label: 'This search',
    entries: [
      { type: 'search', action: 'open' },
      // A segment opens in the filter panel, which cannot show OR or repeats:.
      ...(savable ? [{ type: 'search', action: 'save' } as Entry] : []),
    ],
  });

  if (idle) {
    if (canLeads && recentLeadRows.length) {
      sections.push({ key: 'recent-leads', label: 'Recently viewed', entries: recentLeadRows.map((lead) => ({ type: 'recent-lead', lead })) });
    }
    if (canLeads && recentQueries.length) {
      sections.push({ key: 'recent-queries', label: 'Recent searches', entries: recentQueries.map((q) => ({ type: 'recent-query', query: q })) });
    }
    // Recent items are re-checked against what this person may see now, and
    // a record that has gone from its loaded list is dropped.
    const recentCommands: Command[] = recent.flatMap((r) => {
      const group = groupOf(r.id);
      if (!group) return [];
      const live = byId.get(r.id);
      if (live) return [{ ...live, group: 'recent' as const }];
      if (group === 'pages' || group === 'settings') return [];
      if (!canSeeList(user, group as ListKey) || loaded.has(group as ListKey)) return [];
      const to = routeFor(r.id);
      return to ? [{ id: r.id, group: 'recent' as const, label: r.label, icon: IconClock, run: { kind: 'go' as const, to } }] : [];
    });
    sections.push({ key: 'recent', label: groupLabel(v, 'recent'), entries: recentCommands.map(cmd) });
    for (const key of ['pages', 'actions', 'appearance'] as const) {
      sections.push({ key, label: groupLabel(v, key), entries: all.filter((c) => c.group === key).map(cmd) });
    }
  } else if (!help && chips.length > 0) {
    // A filtered search is a search of records: the lists do not apply.
    if (searching) sections.push(leadsSection());
    if (canLeads && hasFilters) sections.push(searchActions());
  } else if (!help && parsed.text) {
    // Free text. (A filter still being typed, with nothing else, shows only its suggestions.)
    const q = parsed.text;
    // People first: one row per person is usually what a name typed here means.
    if (personRows.length) {
      sections.unshift({
        key: 'people',
        label: groupLabel(v, 'people'),
        entries: personRows.map((person) => ({ type: 'person', person })),
      });
    }
    for (const key of ['pages', 'actions', 'settings'] as const) {
      sections.push({ key, label: groupLabel(v, key), entries: all.filter((c) => c.group === key && matches(c, q)).map(cmd) });
    }
    for (const key of ['segments', 'companies', 'deals', 'tasks', 'campaigns', 'destinations', 'buyers', 'suppliers', 'imports', 'templates'] as const) {
      sections.push({ key, label: groupLabel(v, key), entries: (lists[key] ?? []).filter((c) => matches(c, q)).slice(0, PER_LIST).map(cmd) });
    }
    if (searching) {
      sections.push(leadsSection());
      sections.push({ ...searchActions(), entries: [{ type: 'search', action: 'open' }] });
    }
    sections.push({ key: 'appearance', label: groupLabel(v, 'appearance'), entries: all.filter((c) => c.group === 'appearance' && matches(c, q)).map(cmd) });
  }

  const shown = sections.filter((s) => s.entries.length > 0 || (s.key === 'leads' && s.extra));
  const flat: Entry[] = shown.flatMap((s) => s.entries);
  const suggestionCount = flat.filter((e) => e.type === 'suggestion').length;
  const current = flat[Math.min(sel, flat.length - 1)] as Entry | undefined;

  // The highlight starts at the top whenever the query changes.
  useEffect(() => setSel(0), [text, committed.length]);
  useEffect(() => {
    if (sel >= flat.length && flat.length > 0) setSel(flat.length - 1);
  }, [sel, flat.length]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmdk-index="${sel}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [sel]);
  // Put the caret where an edit left it.
  useLayoutEffect(() => {
    const p = pendingCaret.current;
    if (p === null || !inputRef.current) return;
    pendingCaret.current = null;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(p, p);
  });

  // ---- doing things ---------------------------------------------------
  function rememberQuery() {
    if (hasFilters) setRecentQueries(pushRecentQuery([...committed, text].join(' ')));
  }

  function openInLeads(filters: LeadFilters, saveAs = false) {
    rememberQuery();
    const { search, ...rest } = filters;
    // A search with OR, or repeats:, goes over whole, as written, for the
    // page's read-only "Search:" banner: its filter panel cannot show either.
    const handoff: LeadsHandoff = filters.any_of || filters.repeats_min != null
      ? { filters, search: '', query: joinTerms([...committed, text]) }
      : { filters: saveAs ? filters : rest, search: search ?? '', saveAs };
    spotlight.close();
    navigate('/leads', { state: { cmdk: handoff } });
  }

  function runInterpret() {
    if (interpret.isPending) return;
    interpret.mutate(text.trim(), {
      onSuccess: (res) => {
        if (!res.query.trim()) {
          setInterpretNote('Could not turn that into filters. Try naming a status, a date or a name.');
          return;
        }
        const { terms, text: rest, kept, dropped } = splitInterpreted(res.query, res.unknown, ctx);
        setCommitted(terms);
        setBox(rest ? `${rest}` : '', rest.length);
        const notes = [
          dropped.length ? `Not used: ${dropped.join(', ')}.` : '',
          kept.length ? `Kept as text: ${kept.join(', ')}.` : '',
        ].filter(Boolean).join(' ');
        setInterpretNote(notes || null);
      },
      onError: (e) => notifications.show({ color: 'red', message: (e as Error).message }),
    });
  }

  function choose(c: Command) {
    if (c.run.kind === 'appearance') {
      appearance.set(c.run.value);
      return;
    }
    if (c.run.kind === 'lead') {
      onLead(c.run.id);
      return;
    }
    // Pages, Settings tabs and named records only: never a contact.
    if (groupOf(c.id)) setRecent(pushRecent({ id: c.id, label: c.label }));
    navigate(c.run.to);
  }

  function setBox(next: string, caretAt: number) {
    setText(next);
    setCaret(caretAt);
    pendingCaret.current = caretAt;
  }

  /** Finished filters at the end of the box become chips. */
  function settle(next: string, caretAt: number) {
    setInterpretNote(null);
    if (caretAt === next.length && /\s$/.test(next)) {
      // Once an OR is among the finished terms, order matters for the rest.
      const ordered = committed.some((t) => tokenize(t)[0]?.op);
      const { terms, rest } = commitTerms(next, ctx, ordered);
      if (terms.length) {
        setCommitted((c) => [...c, ...terms]);
        setBox(rest, rest.length);
        return;
      }
    }
    setText(next);
    setCaret(caretAt);
  }

  function accept(s: Suggestion) {
    if (!sugg) return;
    const before = text.slice(0, sugg.start);
    const after = text.slice(sugg.end).replace(/^\s+/, '');
    const head = `${before}${s.insert}${s.done ? ' ' : ''}`;
    if (s.done && !after) settle(head, head.length);
    else setBox(`${head}${after}`, head.length);
    inputRef.current?.focus();
  }

  function activate(e: Entry) {
    switch (e.type) {
      case 'suggestion': accept(e.s); return;
      case 'command': choose(e.cmd); return;
      case 'lead': rememberQuery(); onLead(e.lead.id); return;
      case 'search': openInLeads(body, e.action === 'save'); return;
      case 'recent-query':
        setCommitted(tokenize(e.query).filter((t) => t.key || t.op).map((t) => (t.op === 'or' ? 'OR' : t.raw)));
        setBox('', 0);
        return;
      case 'recent-lead': onLead(e.lead.id); return;
      case 'person': navigate(`/people/${encodeURIComponent(e.person.id)}`); return;
      case 'interpret': runInterpret();
    }
  }

  /** What can be done with the highlighted row, for this person. */
  function quickActions(e: Entry | undefined): QuickAction[] {
    const out: QuickAction[] = [];
    const inLeads = `Open in ${navLabel(v, 'leads')}`;
    if (e?.type === 'lead') {
      // Enter shows the whole search in Leads; the one record is a click or ⌘↵ away.
      if (canLeads) out.push({ id: 'leads', label: inLeads, keys: ['↵'] });
      out.push({ id: 'open', label: 'Open details', keys: canLeads ? [mod, '↵'] : ['↵'] });
      if (e.lead.email) out.push({ id: 'copy-email', label: 'Copy email', keys: [mod, 'C'] });
      if (e.lead.phone1) out.push({ id: 'copy-phone', label: 'Copy phone', keys: [mod, 'P'] });
    } else if (e?.type === 'recent-lead') {
      out.push({ id: 'open', label: 'Open details', keys: ['↵'] });
      if (e.lead.email) out.push({ id: 'copy-email', label: 'Copy email', keys: [mod, 'C'] });
      if (e.lead.phone1) out.push({ id: 'copy-phone', label: 'Copy phone', keys: [mod, 'P'] });
    } else if (e?.type === 'command') {
      out.push({ id: 'open', label: 'Open', keys: ['↵'] });
      const m = e.cmd.meta;
      if (canLeads && (m?.kind === 'campaign' || m?.kind === 'entity')) {
        out.push({ id: 'leads', label: `Show its ${v.many('lead')}`, keys: [mod, '↵'] });
      }
    }
    if (canLeads && savable && e?.type !== 'command') {
      out.push({ id: 'save', label: 'Save as segment', keys: [mod, 'S'] });
    }
    return out;
  }
  const actions = quickActions(current);

  async function copy(value: string, what: string) {
    try {
      await navigator.clipboard.writeText(value);
      notifications.show({ message: `${what} copied`, autoClose: 1500 });
    } catch {
      notifications.show({ color: 'red', message: `Could not copy the ${what.toLowerCase()}` });
    }
  }

  function runQuick(a: QuickAction) {
    const e = current;
    switch (a.id) {
      case 'open':
        listRef.current?.querySelector<HTMLElement>(`[data-cmdk-index="${sel}"]`)?.click();
        return;
      case 'leads': {
        const m = e?.type === 'command' ? e.cmd.meta : undefined;
        if (m?.kind === 'campaign') openInLeads({ campaign_ids: [m.id] });
        else if (m?.kind === 'entity') openInLeads(m.entity === 'buyer' ? { buyer_ids: [m.id] } : { supplier_ids: [m.id] });
        else openInLeads(body);
        return;
      }
      case 'copy-email':
        if (e?.type === 'lead' || e?.type === 'recent-lead') void copy(e.lead.email, 'Email');
        return;
      case 'copy-phone':
        if (e?.type === 'lead' || e?.type === 'recent-lead') void copy(e.lead.phone1, 'Phone');
        return;
      case 'save':
        openInLeads(body, true);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing) return;
    const n = flat.length;
    const isMod = e.metaKey || e.ctrlKey;
    const find = (id: QuickAction['id']) => actions.find((a) => a.id === id);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSel((s) => (n ? (Math.min(s, n - 1) + 1) % n : 0));
        return;
      case 'ArrowUp':
        e.preventDefault();
        setSel((s) => (n ? (Math.min(s, n - 1) - 1 + n) % n : 0));
        return;
      case 'Enter': {
        e.preventDefault();
        const onLeadRow = current?.type === 'lead' && canLeads;
        if (isMod) {
          // On a contact, ⌘↵ is the way to its details, since Enter opens the list.
          if (onLeadRow) { runQuick(find('open') as QuickAction); return; }
          const a = find('leads');
          if (a) runQuick(a);
          else if (canLeads && bodyKey !== null) openInLeads(body);
          return;
        }
        if (onLeadRow) { openInLeads(body); return; }
        listRef.current?.querySelector<HTMLElement>(`[data-cmdk-index="${Math.min(sel, n - 1)}"]`)?.click();
        return;
      }
      case 'Tab': {
        if (suggestionCount === 0) return;
        e.preventDefault();
        const s = current?.type === 'suggestion' ? current.s : (flat[0] as Extract<Entry, { type: 'suggestion' }>).s;
        accept(s);
        return;
      }
      case 'Backspace': {
        const input = e.currentTarget;
        if (text === '' && input.selectionStart === 0 && input.selectionEnd === 0 && committed.length > 0) {
          e.preventDefault();
          setCommitted((c) => tidyTerms(c.slice(0, -1)));
        }
        return;
      }
    }
    if (!isMod || e.shiftKey || e.altKey) return;
    const key = e.key.toLowerCase();
    const input = e.currentTarget;
    // ⌘C copies the highlighted email only when no text is selected in the
    // box, so copying what was typed still works as everywhere else.
    if (key === 'c' && input.selectionStart === input.selectionEnd && find('copy-email')) {
      e.preventDefault();
      runQuick(find('copy-email') as QuickAction);
    } else if (key === 'p' && find('copy-phone')) {
      e.preventDefault();
      runQuick(find('copy-phone') as QuickAction);
    } else if (key === 's' && find('save')) {
      e.preventDefault();
      runQuick(find('save') as QuickAction);
    }
  }

  // ---- rendering --------------------------------------------------------
  function renderEntry(e: Entry): ReactNode {
    switch (e.type) {
      case 'suggestion':
        return (
          <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }}>
            <Text size="sm" ff={e.s.isName ? undefined : 'monospace'} fw={600} style={{ flexShrink: 0 }} data-testid="cmdk-suggestion">{e.s.label}</Text>
            {e.s.detail && <Text size="xs" c="dimmed" truncate>{e.s.detail}</Text>}
          </Group>
        );
      case 'command':
        return (
          <CommandRowContent
            cmd={e.cmd}
            right={e.cmd.run.kind === 'appearance' && appearance.value === e.cmd.run.value
              ? <IconCheck size={16} aria-label="Current" />
              : null}
          />
        );
      case 'lead':
        return <LeadRowContent lead={e.lead} />;
      case 'search': {
        const label = e.action === 'open' ? `Open in ${navLabel(v, 'leads')} with ${hasFilters ? 'these filters' : 'this search'}` : 'Save as segment';
        const Icon = e.action === 'open' ? IconTable : IconDeviceFloppy;
        return (
          <Group gap={10} wrap="nowrap" style={{ width: '100%' }}>
            <Icon size={18} stroke={1.6} style={{ opacity: 0.7 }} />
            <Text size="sm" style={{ flex: 1 }}>{label}</Text>
            <Group gap={3} wrap="nowrap">
              {(e.action === 'open' ? [mod, '↵'] : [mod, 'S']).map((k) => <Kbd key={k} size="xs">{k}</Kbd>)}
            </Group>
          </Group>
        );
      }
      case 'recent-query':
        return (
          <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }}>
            <IconHistory size={18} stroke={1.6} style={{ opacity: 0.7, flexShrink: 0 }} />
            <Text size="sm" ff="monospace" truncate>{e.query}</Text>
          </Group>
        );
      case 'recent-lead':
        return <LeadRowContent lead={e.lead} />;
      case 'person':
        return (
          <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }} data-testid="cmdk-person">
            <IconUserCircle size={18} stroke={1.6} style={{ opacity: 0.7, flexShrink: 0 }} />
            <Text size="sm" fw={500} truncate style={{ flexShrink: 1, minWidth: 0 }}>
              {e.person.name.trim() || e.person.email || e.person.phone}
            </Text>
            <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
              {[e.person.name.trim() ? e.person.email : '', v.count(e.person.submissions, 'lead')].filter(Boolean).join(' · ')}
            </Text>
          </Group>
        );
      case 'interpret':
        return (
          <Group gap={10} wrap="nowrap" style={{ width: '100%', minWidth: 0 }} data-testid="cmdk-interpret">
            {interpret.isPending
              ? <Loader size={16} aria-label="Turning into filters" />
              : <IconSparkles size={18} stroke={1.6} style={{ color: TOKENS.textSecondary, flexShrink: 0 }} aria-label="AI" />}
            <Text size="sm" fw={600} style={{ flexShrink: 0 }}>Turn into filters</Text>
            <Text size="xs" c="dimmed" truncate>{interpret.isPending ? 'Working it out' : `“${text.trim()}”`}</Text>
            <Kbd size="xs" ml="auto">↵</Kbd>
          </Group>
        );
    }
  }

  const closes = (e: Entry) => e.type !== 'suggestion' && e.type !== 'recent-query' && e.type !== 'interpret';
  let index = 0;
  const previewLead = current?.type === 'lead' || current?.type === 'recent-lead' ? current.lead : null;
  const previewCommand = current?.type === 'command' ? current.cmd : null;
  const nothing = !help && flat.length === 0 && !busy && !(searching && shown.some((s) => s.key === 'leads'));

  return (
    <>
      <div style={{ borderBottom: `1px solid ${TOKENS.border}` }}>
        <Group gap="sm" px="md" py={12} wrap="nowrap">
          <IconSearch size={18} stroke={1.6} style={{ color: TOKENS.textMuted, flexShrink: 0 }} />
          <input
            ref={inputRef}
            data-autofocus
            value={text}
            onChange={(e) => settle(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
            onKeyDown={onKeyDown}
            // An edit of ours that has not reached the box yet wins over a
            // selection event reporting where the caret used to be.
            onSelect={(e) => { if (pendingCaret.current === null) setCaret(e.currentTarget.selectionStart ?? 0); }}
            placeholder={committed.length ? 'Add a filter or search' : `Search ${v.many('lead')}, pages and more. Type ? for filters`}
            aria-label={`Search ${v.many('lead')}, pages and more`}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent',
              color: TOKENS.text, fontSize: 16, fontFamily: 'inherit', padding: 0,
            }}
          />
          {busy && <Loader size={14} />}
        </Group>
        <ChipRow
          items={rowItems}
          onRemove={(i) => setCommitted((c) => tidyTerms(c.filter((_, j) => j !== i)))}
          v={v}
        />
        {errorText && (
          <Text size="xs" px="md" pb={8} style={{ color: TOKENS.redText }} role="status" data-testid="cmdk-error">{errorText}</Text>
        )}
        {interpretNote && (
          <Text size="xs" px="md" pb={8} c="dimmed" role="status" data-testid="cmdk-interpret-note">{interpretNote}</Text>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 0 }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }} ref={listRef}>
          <Spotlight.ActionsList aria-label="Results">
            {help && <HelpPanel v={v} people={canPeople} ownership={ownership} scoring={scoring} onExample={(ex) => { setCommitted([]); settle(`${ex} `, ex.length + 1); }} />}
            {shown.map((s) => (
              <SectionView key={s.key} id={`cmdk-${s.key}`} label={s.label} extra={s.extra}>
                {s.entries.map((entry) => {
                  const i = index++;
                  return (
                    <Spotlight.Action
                      key={`${s.key}:${i}`}
                      data-cmdk-index={i}
                      data-selected={i === sel || undefined}
                      aria-selected={i === sel}
                      closeSpotlightOnTrigger={closes(entry)}
                      onMouseMove={() => { if (i !== sel) setSel(i); }}
                      onClick={() => activate(entry)}
                      className="cmdk-row"
                      style={{ padding: '6px 10px' }}
                    >
                      {renderEntry(entry)}
                    </Spotlight.Action>
                  );
                })}
                {s.entries.length === 0 && s.empty && (
                  <Text size="sm" c="dimmed" px="md" py={6}>{s.empty}</Text>
                )}
              </SectionView>
            ))}
            {nothing && (
              <Text ta="center" c="dimmed" size="sm" py="lg">Nothing matches that.</Text>
            )}
          </Spotlight.ActionsList>
        </div>
        {wide && !help && (
          <PreviewPane lead={previewLead} command={previewCommand} actions={actions} onAction={runQuick} />
        )}
      </div>
      <Spotlight.Footer>
        <Group gap="md" wrap="wrap">
          <Hint keys={['↑', '↓']} label="to move" />
          <Hint keys={['↵']} label={current?.type === 'suggestion' ? 'to choose' : current?.type === 'lead' && canLeads ? `to open in ${navLabel(v, 'leads')}` : 'to open'} />
          {suggestionCount > 0 && <Hint keys={['Tab']} label="to complete" />}
          {/* Narrow screens keep to the essentials; the keys still work. */}
          {actions.filter((a) => a.keys.join() !== '↵' && (wide || a.id === 'leads' || a.id === 'open')).map((a) => (
            <Hint key={a.id} keys={a.keys} label={a.label.charAt(0).toLowerCase() + a.label.slice(1)} />
          ))}
          {!help && <Hint keys={['?']} label="for filters" />}
          <Hint keys={['Esc']} label="to close" />
        </Group>
      </Spotlight.Footer>
    </>
  );
}

function Hint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      {keys.map((k) => <Kbd key={k} size="xs">{k}</Kbd>)}
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}

/** A labelled group of results, with room beside the label for the count and speed chip. */
function SectionView({ id, label, extra, children }: { id: string; label: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <div role="group" aria-labelledby={id} style={{ marginTop: 'var(--mantine-spacing-xs)' }}>
      <Group justify="space-between" wrap="nowrap" gap="xs" px="md" py={4}>
        <Text id={id} size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
          {label}
        </Text>
        {extra}
      </Group>
      {children}
    </div>
  );
}
